import {
	Badge,
	Box,
	Button,
	Icons,
	Stack,
	Table,
	Text,
	Time,
	joinAttributes,
	Container,
	Inline,
} from "@sembark-travel/ui-base"
import {
	CheckboxInputField,
	DateRangePickerField,
	SelectInputField,
	TextInputField,
} from "@sembark-travel/ui-form"
import {
	CursorListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import {
	Link,
	queryToSearch,
	useLocationQuery,
} from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import { useEffect, useMemo } from "react"
import useSWR from "swr"
import { generatePath } from "../router-utils"
import { TAuthLog, TAuthLogsRequest, authLogsXHR } from "./store"
import {
	AUTH_LOG_EVENTS,
	TAuthLogFilters,
	TAuthLogFiltersInQuery,
	authLogEventLabel,
	authLogFailureLabel,
	authLogFiltersToQuery,
	authLogFiltersToRequest,
	authLogQueryToFilters,
} from "./utils"

/**
 * Login and account-security events with totals for the period. Without a
 * tenant, covers every tenant the staff member looks after (pass
 * TenantsFilter to filter by tenant); with one, only that tenant's users.
 */
export function AuthLogsList({
	tenantUid,
	TenantsFilter,
}: {
	tenantUid?: string
	TenantsFilter?: React.ComponentType
}) {
	const [query, setQuery] = useLocationQuery<
		TAuthLogFilters,
		TAuthLogFiltersInQuery
	>({
		toQuery: authLogFiltersToQuery,
		fromQuery: authLogQueryToFilters,
	})
	const [params, setParams] = useSearch<TAuthLogFilters>(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const Filters = useMemo(
		() =>
			function AuthLogFilters() {
				return (
					<Stack gap="4">
						{TenantsFilter ? <TenantsFilter /> : null}
						<CommonFilters />
					</Stack>
				)
			},
		[TenantsFilter]
	)
	const request = useMemo(
		() => ({ ...authLogFiltersToRequest(params), tenant_uid: tenantUid }),
		[params, tenantUid]
	)
	return (
		<Search
			title="Auth Logs"
			placeholder="Search by email, name or IP..."
			initialParams={params}
			onSearch={(params) => setParams({ ...params, cursor: null })}
			Filters={Filters}
		>
			<Stack gap="4" paddingY="4">
				<AuthLogsSummary request={request} />
				<CursorListView<TAuthLog, TAuthLogFilters>
					pageKey={`auth-logs-${tenantUid || "all"}`}
					params={params}
					fetch={(xhr, params) =>
						authLogsXHR(xhr).get({
							...authLogFiltersToRequest(params),
							tenant_uid: tenantUid,
						})
					}
					onCursorChange={(cursor) => setParams({ ...params, cursor })}
					previousPageLabel="Newer"
					nextPageLabel="Older"
				>
					{({ items }) => (
						<AuthLogsTable logs={items} hideTenant={Boolean(tenantUid)} />
					)}
				</CursorListView>
			</Stack>
		</Search>
	)
}

/**
 * Security log of one account, newest first.
 */
export function AuthLogs({
	userId,
	filters,
}: {
	userId: number
	/**
	 * Narrow the log, e.g. to login attempts from outside India in a month
	 */
	filters?: Omit<TAuthLogsRequest, "cursor" | "user_id">
}) {
	const [params, setParams] = useSearch<TSearchParams>({})
	return (
		<CursorListView<TAuthLog, TSearchParams>
			pageKey={`auth-logs-user-${userId}${queryToSearch(filters || {})}`}
			params={params}
			fetch={(xhr, params) =>
				authLogsXHR(xhr).get({
					...filters,
					cursor: params.cursor,
					user_id: userId,
				})
			}
			onCursorChange={(cursor) => setParams({ ...params, cursor })}
			removeContainerPadding
			previousPageLabel="Newer"
			nextPageLabel="Older"
			emptyState={({ onRefresh }) => (
				<Text>
					No security logs yet!{" "}
					<Button onClick={() => onRefresh()} inline>
						<Icons.Refresh />
					</Button>
				</Text>
			)}
		>
			{({ items }) => <AuthLogsTable logs={items} hideTenant hideUser />}
		</CursorListView>
	)
}

function AuthLogsSummary({ request }: { request: TAuthLogsRequest }) {
	const xhr = useXHR()
	const { data, error } = useSWR(
		`/auth-logs/summary${queryToSearch(request)}`,
		() => authLogsXHR(xhr).getSummary(request)
	)
	if (error) {
		return <Text color="danger">{error.message}</Text>
	}
	const stats: Array<[string, number | undefined, string?]> = [
		["Logins", data?.logins],
		["Active Users", data?.unique_users],
		["Failed Logins", data?.failed_logins],
		["Locked Out", data?.locked_out],
		["Logins Outside India", data?.foreign_logins],
		["Unknown Country", data?.unknown_country_logins],
	]
	return (
		<Container>
			<Stack gap="1">
				<Text fontSize="sm" color="muted">
					{request.created_after || request.created_before
						? "For the selected period"
						: "Last 30 days"}
				</Text>
				<Inline
					flexWrap="wrap"
					bgColor="default"
					rounded="md"
					borderWidth="1"
					paddingY="2"
				>
					{stats.map(([label, value], index) => (
						<Stack
							key={label}
							gap="px"
							borderLeftWidth={index > 0 ? "1" : undefined}
							paddingX="4"
							paddingY="2"
						>
							<Text fontSize="sm" color="muted">
								{label}
							</Text>
							<Box fontSize="2xl" fontWeight="semibold">
								{value === undefined ? "…" : value}
							</Box>
						</Stack>
					))}
				</Inline>
			</Stack>
		</Container>
	)
}

function AuthLogsTable({
	logs,
	hideTenant,
	hideUser,
}: {
	logs: Array<TAuthLog>
	hideTenant: boolean
	hideUser?: boolean
}) {
	return (
		<Table
			bordered
			responsive
			striped
			headers={[
				"Time",
				...(hideTenant ? [] : ["Tenant"]),
				...(hideUser ? [] : ["User"]),
				"Event",
				"IP / Country",
				"Device",
			]}
			rows={logs.map((log) => [
				<Time timestamp={log.created_at} dateFormat timeFormat />,
				...(hideTenant
					? []
					: [
							log.tenant ? (
								<Link
									to={generatePath("/admin/tenants/:tenantId", {
										tenantId: log.tenant.id.toString(),
									})}
									color="accent"
								>
									{log.tenant.name}
								</Link>
							) : (
								<Text color="muted">-</Text>
							),
						]),
				...(hideUser
					? []
					: [
							log.user ? (
								<Text fontWeight="semibold">{log.user.name}</Text>
							) : log.identifier ? (
								<Text
									color="danger"
									title="Attempted email: no account with this email"
								>
									{log.identifier}
								</Text>
							) : (
								<Text color="muted">-</Text>
							),
						]),
				<Stack gap="px">
					<Box>
						<Badge
							danger={log.event.endsWith("_failed")}
							warning={log.event === "login_locked_out"}
						>
							{authLogEventLabel(log.event)}
						</Badge>
					</Box>
					<Text fontSize="sm" color="muted">
						{joinAttributes(
							authLogFailureLabel(log.failure_reason),
							log.method,
							log.mfa ? "2FA" : null,
							log.actor ? `by ${log.actor.name}` : null
						)}
					</Text>
					<AuthLogMetadata log={log} />
				</Stack>,
				<Stack gap="px">
					<Text>{log.ip || "-"}</Text>
					<Text
						fontSize="sm"
						color={
							log.country_code && log.country_code !== "IN"
								? "warning"
								: "muted"
						}
						fontWeight={
							log.country_code && log.country_code !== "IN"
								? "semibold"
								: undefined
						}
					>
						{/* country only: city and region stay out of the logs */}
						{log.country_code || "Unknown"}
					</Text>
				</Stack>,
				<Stack gap="px" title={log.user_agent || undefined}>
					<Text>{joinAttributes(log.browser, log.os) || "-"}</Text>
					{log.device_type ? (
						<Text fontSize="sm" color="muted">
							{log.device_type}
						</Text>
					) : null}
				</Stack>,
			])}
		/>
	)
}

function AuthLogMetadata({ log }: { log: TAuthLog }) {
	const metadata = log.metadata || {}
	const details: Array<string> = []
	if (typeof metadata.old_email === "string") {
		details.push(`${metadata.old_email} → ${String(metadata.new_email)}`)
	}
	if (typeof metadata.reason === "string") {
		details.push(metadata.reason.replace(/_/g, " "))
	}
	if (typeof metadata.can_disable_2fa === "boolean") {
		details.push(
			metadata.can_disable_2fa ? "can disable 2FA" : "can not disable 2FA"
		)
	}
	if (!details.length) return null
	return (
		<Text fontSize="sm" color="muted">
			{details.join(" · ")}
		</Text>
	)
}

function CommonFilters() {
	return (
		<Stack gap="4">
			<DateRangePickerField
				fromName="created_after"
				toName="created_before"
				label="Between"
				dateFormat="DD MMM, YYYY"
			/>
			<SelectInputField name="event" label="Event">
				<option value="">All events</option>
				{AUTH_LOG_EVENTS.map(([value, label]) => (
					<option key={value} value={value}>
						{label}
					</option>
				))}
			</SelectInputField>
			<TextInputField
				name="country"
				label="Country Code"
				placeholder="e.g. IN, US"
				maxLength={2}
			/>
			<CheckboxInputField name="outside_india" label="Outside India only" />
		</Stack>
	)
}
